import { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Logo } from "@/components/Logo";
import { UpgradeModal } from "@/components/UpgradeModal";
import { PlayBillingAutoRestore } from "@/components/PlayBillingAutoRestore";
import { LangToggle } from "@/components/LangToggle";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, ScanLine, ReceiptText, CreditCard, Settings,
  ShieldCheck, LogOut, Menu, Crown, Sparkles,
} from "lucide-react";

const navItems = [
  { to: "/dashboard", key: "nav.dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/scan", key: "nav.scan", icon: ScanLine, testid: "nav-scan" },
  { to: "/receipts", key: "nav.receipts", icon: ReceiptText, testid: "nav-receipts" },
  { to: "/subscriptions", key: "nav.subscriptions", icon: CreditCard, testid: "nav-subscriptions" },
  { to: "/settings", key: "nav.settings", icon: Settings, testid: "nav-settings" },
];

function NavList({ onNavigate, isAdmin, t }) {
  const base = "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors duration-150";
  const inactive = "text-muted-foreground hover:text-foreground hover:bg-white/5";
  const active = "bg-cyan-400/10 text-cyan-200 border border-cyan-400/20";
  return (
    <nav className="flex flex-col gap-1" data-testid="app-sidebar-nav">
      {navItems.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          onClick={onNavigate}
          data-testid={it.testid}
          className={({ isActive }) => `${base} ${isActive ? active : inactive}`}
        >
          <it.icon style={{ width: 18, height: 18 }} />
          {t(it.key)}
        </NavLink>
      ))}
      {isAdmin && (
        <NavLink
          to="/admin"
          onClick={onNavigate}
          data-testid="nav-admin"
          className={({ isActive }) => `${base} ${isActive ? active : inactive}`}
        >
          <ShieldCheck style={{ width: 18, height: 18 }} />
          {t("nav.admin")}
        </NavLink>
      )}
    </nav>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const SidebarInner = ({ onNavigate }) => (
    <div className="flex h-full flex-col">
      <div className="px-4 py-5 flex items-center justify-between">
        <button onClick={() => { navigate("/dashboard"); onNavigate?.(); }} className="outline-none">
          <Logo />
        </button>
      </div>
      <div className="px-3 flex-1 overflow-y-auto">
        <NavList onNavigate={onNavigate} isAdmin={user?.is_admin} t={t} />
      </div>
      <div className="p-3 border-t border-white/10 space-y-3">
        {!user?.is_premium && (
          <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-3">
            <div className="flex items-center gap-2 text-cyan-200 text-sm font-medium">
              <Sparkles style={{ width: 16, height: 16 }} /> {t("common.freePlan")}
            </div>
            <p className="text-xs text-muted-foreground mt-1 mb-2">{t("sidebar.freePlanDesc")}</p>
            <Button
              data-testid="sidebar-upgrade-button"
              onClick={() => { setUpgradeOpen(true); onNavigate?.(); }}
              className="w-full bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold h-9"
            >
              <Crown style={{ width: 16, height: 16 }} className="mr-1.5" /> {t("common.upgrade")}
            </Button>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 px-1">
          <span className="text-xs text-muted-foreground">{t("settings.language")}</span>
          <LangToggle />
        </div>
        <div className="flex items-center gap-3 px-1">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-9 w-9 rounded-full border border-white/10" />
          ) : (
            <div className="h-9 w-9 rounded-full bg-cyan-400/15 border border-cyan-400/25 flex items-center justify-center text-cyan-200 text-sm font-semibold">
              {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{user?.name || "User"}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <button onClick={logout} data-testid="logout-button" aria-label={t("common.signOut")} className="text-muted-foreground hover:text-rose-300 p-1.5">
            <LogOut style={{ width: 18, height: 18 }} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-white/10 bg-surface-0 z-30">
        <SidebarInner />
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-white/10 bg-surface-0/90 backdrop-blur px-4 h-14">
        <Logo size="sm" />
        <div className="flex items-center gap-2">
          {!user?.is_premium && (
            <Button size="sm" data-testid="mobile-upgrade-button" onClick={() => setUpgradeOpen(true)} className="h-8 bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold">
              <Crown style={{ width: 14, height: 14 }} className="mr-1" /> {t("common.premium")}
            </Button>
          )}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button" className="h-9 w-9">
                <Menu style={{ width: 20, height: 20 }} />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 bg-surface-0 border-white/10">
              <SidebarInner onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="lg:pl-64">
        <div className="mx-auto max-w-[1200px] px-4 md:px-8 py-6 md:py-8 pb-32 lg:pb-10">
          <Outlet context={{ openUpgrade: () => setUpgradeOpen(true) }} />
        </div>
      </main>

      <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />
      <PlayBillingAutoRestore />
    </div>
  );
}
